import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import './Search.css';

function formatPriceFull(p) {
  if (!p) return null;
  return '$' + p.toLocaleString();
}

const statusLabel = {
  listed: 'accepting bids',
  new: 'new listing',
  'off-market': 'off-market',
};

const statusBadge = {
  listed: 'badge-blue',
  new: 'badge-green',
  'off-market': 'badge-amber',
};

function ListingCard({ listing }) {
  const navigate = useNavigate();
  const isOffMarket = listing.status === 'off-market';

  return (
    <div className="listing-card" onClick={() => navigate(`/bid/${listing.id}`)}>
      <div className="card-img" style={{ background: '#dde8f2' }}>
        <svg className="house-svg" viewBox="0 0 80 60">
          <polygon points="40,5 75,35 65,35 65,58 15,58 15,35 5,35" fill="#185FA5" opacity="0.35"/>
          <rect x="30" y="38" width="20" height="20" fill="#185FA5" opacity="0.5"/>
        </svg>
        <span className={`badge ${statusBadge[listing.status] || 'badge-gray'}`}>
          {statusLabel[listing.status] || listing.status}
        </span>
      </div>
      <div className="card-body">
        <div className="card-price">
          {isOffMarket
            ? `est. ${formatPriceFull(listing.zestimate)}`
            : formatPriceFull(listing.asking_price)}
        </div>
        <div className="card-addr">{listing.address}</div>
        <div className="card-city">{listing.city}, {listing.state} {listing.zip}</div>
        <div className="card-meta">
          <span>{listing.beds} bd</span>
          <span>{listing.baths} ba</span>
          <span>{listing.sqft?.toLocaleString()} sqft</span>
        </div>
        <div className="card-zest">
          Zestimate: {formatPriceFull(listing.zestimate)}
        </div>
        <div className="card-actions">
          <button
            className={`btn btn-sm ${isOffMarket ? '' : 'btn-primary'}`}
            onClick={e => { e.stopPropagation(); navigate(`/bid/${listing.id}`); }}
          >
            {isOffMarket ? 'notify owner' : 'place bid'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Search() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('Houston, TX');
  const [activeFilter, setActiveFilter] = useState('all');
  const [minBeds, setMinBeds] = useState('any');
  const [maxPrice, setMaxPrice] = useState('any');
  const [sort, setSort] = useState('best');

  useEffect(() => {
    fetchProperties();
  }, [activeFilter, minBeds, maxPrice]);

  async function fetchProperties() {
    setLoading(true);
    setError(null);

    let q = supabase
      .from('properties')
      .select('*')
      .order('created_at', { ascending: false });

    if (activeFilter !== 'all') {
      q = q.eq('status', activeFilter);
    }
    if (minBeds !== 'any') {
      q = q.gte('beds', parseInt(minBeds));
    }
    if (maxPrice !== 'any') {
      q = q.lte('asking_price', parseInt(maxPrice) * 1000);
    }

    const { data, error } = await q;

    if (error) {
      setError(error.message);
    } else {
      setProperties(data || []);
    }
    setLoading(false);
  }

  const filters = [
    { key: 'all', label: 'all homes' },
    { key: 'listed', label: 'accepting bids' },
    { key: 'new', label: 'new listings' },
    { key: 'off-market', label: 'off-market' },
  ];

  const sorted = [...properties].sort((a, b) => {
    if (sort === 'price-asc') return (a.asking_price || a.zestimate) - (b.asking_price || b.zestimate);
    if (sort === 'price-desc') return (b.asking_price || b.zestimate) - (a.asking_price || a.zestimate);
    return 0;
  });

  return (
    <div className="search-page">
      <div className="search-header">
        <div className="search-bar">
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="City, zip, neighborhood, or address"
            onKeyDown={e => e.key === 'Enter' && fetchProperties()}
          />
          <select value={maxPrice} onChange={e => setMaxPrice(e.target.value)}>
            <option value="any">any price</option>
            <option value="500">under $500k</option>
            <option value="750">under $750k</option>
            <option value="1000">under $1M</option>
          </select>
          <select value={minBeds} onChange={e => setMinBeds(e.target.value)}>
            <option value="any">any beds</option>
            <option value="2">2+ beds</option>
            <option value="3">3+ beds</option>
            <option value="4">4+ beds</option>
          </select>
          <button className="btn btn-primary" onClick={fetchProperties}>search</button>
        </div>
        <div className="filter-row">
          {filters.map(f => (
            <button
              key={f.key}
              className={`filter-chip ${activeFilter === f.key ? 'active' : ''}`}
              onClick={() => setActiveFilter(f.key)}
            >{f.label}</button>
          ))}
        </div>
      </div>

      <div className="search-layout">
        <div className="search-results">
          <div className="results-header">
            <span>
              {loading ? 'loading...' : error ? `error: ${error}` : `${sorted.length} homes near ${query}`}
            </span>
            <select value={sort} onChange={e => setSort(e.target.value)} className="sort-select">
              <option value="best">sort: best match</option>
              <option value="price-asc">price: low to high</option>
              <option value="price-desc">price: high to low</option>
            </select>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
              loading properties...
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#991B1B' }}>
              {error}
            </div>
          ) : sorted.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
              no properties found. try adjusting your filters.
            </div>
          ) : (
            <div className="listings-grid">
              {sorted.map(l => <ListingCard key={l.id} listing={l} />)}
            </div>
          )}

          <div className="search-owner-cta card" style={{ marginTop: 28 }}>
            <h3>Are you a homeowner?</h3>
            <p>Claim your home to see buyer interest, get pricing guidance, and field offers — on your schedule.</p>
            <div className="owner-steps">
              {[
                'Claim your home address to see buyer interest in your area',
                'Set a notify-me price — get alerted when bids hit your number',
                'List publicly or keep it quiet and field offers privately'
              ].map((s, i) => (
                <div key={i} className="owner-step">
                  <span className="step-num">{i + 1}</span><span>{s}</span>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
              <a href="/owner-list" className="btn btn-primary btn-sm">claim my home</a>
              <a href="/owner-dashboard" className="btn btn-sm">see buyer activity near me</a>
            </div>
          </div>
        </div>

        <div className="map-panel">
          <div className="map-placeholder">
            <svg viewBox="0 0 320 440" width="100%" xmlns="http://www.w3.org/2000/svg">
              <rect width="320" height="440" fill="#eef2f7" rx="12"/>
              <rect x="0" y="80" width="320" height="10" fill="#dde4ee" opacity="0.7"/>
              <rect x="0" y="180" width="320" height="10" fill="#dde4ee" opacity="0.7"/>
              <rect x="0" y="280" width="320" height="10" fill="#dde4ee" opacity="0.7"/>
              <rect x="0" y="370" width="320" height="10" fill="#dde4ee" opacity="0.7"/>
              <rect x="70" y="0" width="10" height="440" fill="#dde4ee" opacity="0.7"/>
              <rect x="180" y="0" width="10" height="440" fill="#dde4ee" opacity="0.7"/>
              <rect x="270" y="0" width="10" height="440" fill="#dde4ee" opacity="0.7"/>
              <rect x="10" y="90" width="52" height="80" rx="4" fill="#fff" stroke="#dde4ee" strokeWidth="0.5"/>
              <rect x="82" y="90" width="88" height="80" rx="4" fill="#fff" stroke="#dde4ee" strokeWidth="0.5"/>
              <rect x="10" y="190" width="52" height="80" rx="4" fill="#fff" stroke="#dde4ee" strokeWidth="0.5"/>
              <rect x="82" y="190" width="88" height="80" rx="4" fill="#fff" stroke="#dde4ee" strokeWidth="0.5"/>
              <rect x="190" y="20" width="60" height="52" rx="4" fill="#fff" stroke="#dde4ee" strokeWidth="0.5"/>
              <rect x="190" y="90" width="90" height="80" rx="4" fill="#fff" stroke="#dde4ee" strokeWidth="0.5"/>
              <rect x="190" y="190" width="60" height="80" rx="4" fill="#fff" stroke="#dde4ee" strokeWidth="0.5"/>
              <rect x="260" y="190" width="50" height="80" rx="4" fill="#fff" stroke="#dde4ee" strokeWidth="0.5"/>
              <rect x="10" y="290" width="150" height="70" rx="4" fill="#fff" stroke="#dde4ee" strokeWidth="0.5"/>
              <rect x="190" y="290" width="120" height="70" rx="4" fill="#fff" stroke="#dde4ee" strokeWidth="0.5"/>
              <rect x="10" y="385" width="300" height="45" rx="4" fill="#fff" stroke="#dde4ee" strokeWidth="0.5"/>
              <circle cx="110" cy="132" r="16" fill="#185FA5"/>
              <text x="110" y="137" textAnchor="middle" fontSize="10" fill="#fff" fontFamily="Arial">$485k</text>
              <circle cx="240" cy="132" r="16" fill="#185FA5"/>
              <text x="240" y="137" textAnchor="middle" fontSize="10" fill="#fff" fontFamily="Arial">$720k</text>
              <circle cx="220" cy="232" r="16" fill="#EF9F27"/>
              <text x="220" y="237" textAnchor="middle" fontSize="9" fill="#412402" fontFamily="Arial">est.</text>
              <circle cx="270" cy="46" r="16" fill="#185FA5"/>
              <text x="270" y="51" textAnchor="middle" fontSize="9" fill="#fff" fontFamily="Arial">$1.1M</text>
              <circle cx="36" cy="325" r="16" fill="#185FA5"/>
              <text x="36" y="330" textAnchor="middle" fontSize="9" fill="#fff" fontFamily="Arial">$543k</text>
              <circle cx="250" cy="325" r="16" fill="#185FA5"/>
              <text x="250" y="330" textAnchor="middle" fontSize="9" fill="#fff" fontFamily="Arial">$389k</text>
            </svg>
          </div>
          <div className="map-legend">
            <span><span className="legend-dot blue"/>listed</span>
            <span><span className="legend-dot amber"/>off-market</span>
          </div>
          <p className="map-note">Interactive map coming soon — pins show all listings and off-market homes in your area.</p>
        </div>
      </div>
    </div>
  );
}
